import { BUDGET_CHARGE_CATEGORIES, FUND_CATEGORIES, } from '../budgetPlanning.js';
import { dateStringParts } from '../dateString.js';
import { BUDGET_YEAR_OFFSET } from './budget.js';
import { ZERO_AMOUNT, addYearAmounts, yearTotal, } from './viewModel.js';
export function incomeRole(group, kind) {
    if (BUDGET_CHARGE_CATEGORIES[kind].has(group.label))
        return 'charges';
    if (FUND_CATEGORIES.has(group.label))
        return 'fund';
    return 'other';
}
export function setChargeLines(budget, scope) {
    return budget.income
        .filter((group) => incomeRole(group, scope.kind) === 'charges')
        .flatMap((group) => group.lines)
        .filter((line) => !scope.edits.has(line.id));
}
const budgetYearOf = (row) => yearTotal(row.years[BUDGET_YEAR_OFFSET] ?? ZERO_AMOUNT);
const sumYear = (rows, offset) => rows.reduce((sum, row) => sum + yearTotal(row.years[offset] ?? ZERO_AMOUNT), 0);
const sumMonth = (rows, at) => rows.reduce((sum, row) => {
    const month = row.months[at.offset]?.[at.monthIndex] ?? ZERO_AMOUNT;
    return sum + yearTotal(month) - month.accrued;
}, 0);
const MONTHS_A_YEAR = 12;
function monthPositionOf(model, date) {
    const { year, month } = dateStringParts(date);
    for (let offset = 0; offset < model.monthsOf.length; offset += 1) {
        const monthIndex = model.monthsOf[offset]?.findIndex((m) => m.year === year && m.month === month) ?? -1;
        if (monthIndex !== -1)
            return { offset, monthIndex };
    }
    return undefined;
}
function nextBilled(model, line, from, through) {
    let { offset, monthIndex } = from;
    for (let counted = 0; counted <= through; counted += 1) {
        const calendar = model.monthsOf[offset];
        if (calendar === undefined)
            return undefined;
        if (monthIndex >= calendar.length) {
            offset += 1;
            monthIndex = 0;
            counted -= 1;
            continue;
        }
        if (sumMonth([line], { offset, monthIndex }) !== 0)
            return { offset, monthIndex };
        monthIndex += 1;
    }
    return undefined;
}
function monthsBetween(model, from, to) {
    let months = 0;
    let { offset, monthIndex } = from;
    while (offset < to.offset || monthIndex < to.monthIndex) {
        const calendar = model.monthsOf[offset];
        if (calendar === undefined)
            return months;
        monthIndex += 1;
        months += 1;
        if (monthIndex >= calendar.length) {
            offset += 1;
            monthIndex = 0;
        }
    }
    return months;
}
function annualizedCharge(model, line, start) {
    const first = nextBilled(model, line, start, monthsThroughBudgetYear(model, start));
    if (first === undefined)
        return undefined;
    const after = nextMonth(model, first);
    const second = after === undefined ? undefined : nextBilled(model, line, after, MONTHS_A_YEAR - 1);
    const cadence = second === undefined
        ? MONTHS_A_YEAR
        : Math.min(MONTHS_A_YEAR, Math.max(1, monthsBetween(model, first, second)));
    return (sumMonth([line], first) * MONTHS_A_YEAR) / cadence;
}
function nextMonth(model, at) {
    const calendar = model.monthsOf[at.offset];
    if (calendar === undefined)
        return undefined;
    if (at.monthIndex + 1 < calendar.length)
        return { offset: at.offset, monthIndex: at.monthIndex + 1 };
    return model.monthsOf[at.offset + 1] === undefined
        ? undefined
        : { offset: at.offset + 1, monthIndex: 0 };
}
function monthsThroughBudgetYear(model, start) {
    const last = model.monthsOf[BUDGET_YEAR_OFFSET];
    if (last === undefined)
        return 0;
    return monthsBetween(model, start, { offset: BUDGET_YEAR_OFFSET, monthIndex: last.length - 1 });
}
export function budgetYearInputs(budget, scope) {
    const byRole = (model, role) => model.income.filter((group) => incomeRole(group, scope.kind) === role);
    const chargeLines = byRole(budget, 'charges').flatMap((group) => group.lines);
    const typed = chargeLines.filter((line) => scope.edits.has(line.id));
    const scheduledCharges = byRole(scope.projected, 'charges').flatMap((group) => group.lines);
    const start = monthPositionOf(scope.projected, scope.today);
    const todays = (lines) => lines.reduce((sum, line) => {
        const annual = start === undefined ? undefined : annualizedCharge(scope.projected, line, start);
        return sum + (annual ?? budgetYearOf(line));
    }, 0);
    return {
        kind: scope.kind,
        expenses: sumYear(budget.expense, BUDGET_YEAR_OFFSET),
        otherIncome: sumYear(byRole(budget, 'other'), BUDGET_YEAR_OFFSET),
        currentCharges: todays(scheduledCharges),
        currentSetCharges: todays(setChargeLines(scope.projected, scope)),
        typedCharges: typed.reduce((sum, line) => sum + budgetYearOf(line), 0),
        units: scope.units,
    };
}
export function cumulativeTo(years, months, point) {
    const before = years.slice(0, point.offset).reduce(addYearAmounts, ZERO_AMOUNT);
    const series = months[point.offset];
    const within = series === undefined
        ? (years[point.offset] ?? ZERO_AMOUNT)
        : series.slice(0, point.monthIndex + 1).reduce(addYearAmounts, ZERO_AMOUNT);
    return addYearAmounts(before, within);
}
export function windowAmounts(amount) {
    return {
        actual: amount.actual,
        scheduled: amount.scheduled,
        estimated: amount.inflation + amount.adjustment + amount.accrued,
    };
}
//# sourceMappingURL=budgetYear.js.map